import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { COMPLETE, ERROR, IN_PROGRESS } from '@/lib/constants/actionStatuses'
import { PATH_QUICKBOOKS_CONNECTIONS } from '@/lib/constants/api'
import buildInitialState from '@/store/helpers/buildInitialState'
import updateStatus from '@/store/helpers/updateStatus'
import { notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

const withProgress = (dispatch, message, request, failMessage) => {
  showProgressModal({ dispatch, message })

  return request()
    .then(json => json.data)
    .catch(error => {
      notifyError(dispatch, error.message || failMessage)
      throw error
    })
    .finally(() => {
      hideProgressModal({ dispatch })
    })
}

const quickBooksConnections = {
  namespaced: true,
  state: buildInitialState({
    connections: [],
    configurationError: null
  }),
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    setOverview: (state, { configurationError, connections }) => {
      state.configurationError = configurationError || null
      state.connections = Array.isArray(connections) ? connections : []
    },
    setConnection: (state, connection) => {
      state.connections = state.connections.map(c =>
        c.businessEntityId === connection.businessEntityId ? connection : c)
    }
  },
  actions: {
    getConnections ({ commit, dispatch }) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Loading QuickBooks connections, please wait...' })

      return api.doGet({ path: PATH_QUICKBOOKS_CONNECTIONS })
        .then(json => json.data || {})
        .then(overview => {
          commit('setOverview', overview)
          commit('updateStatus', { status: COMPLETE })
        })
        .catch(error => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to load QuickBooks connections')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    connect ({ dispatch }, { businessEntityId }) {
      return withProgress(
        dispatch,
        'Opening QuickBooks sign-in...',
        () => api.doPost({ path: `${PATH_QUICKBOOKS_CONNECTIONS}/${businessEntityId}/connect` }),
        'Unable to start connecting QuickBooks.'
      ).then(data => data.authorizationUrl)
    },
    disconnect ({ commit, dispatch }, { businessEntityId }) {
      return withProgress(
        dispatch,
        'Disconnecting QuickBooks...',
        () => api.doPost({ path: `${PATH_QUICKBOOKS_CONNECTIONS}/${businessEntityId}/disconnect` }),
        'Unable to disconnect QuickBooks.'
      ).then(result => {
        commit('setConnection', result.connection)
        return result
      })
    },
    updateNotificationRecipients ({ commit, dispatch }, { businessEntityId, notificationRecipients }) {
      return withProgress(
        dispatch,
        'Saving notification recipients...',
        () => api.doPut({
          path: `${PATH_QUICKBOOKS_CONNECTIONS}/${businessEntityId}/notification-recipients`,
          data: { notificationRecipients }
        }),
        'Unable to save notification recipients.'
      ).then(connection => {
        commit('setConnection', connection)
        return connection
      })
    },
    listCustomers (context, { businessEntityId }) {
      return api.doGet({ path: `${PATH_QUICKBOOKS_CONNECTIONS}/${businessEntityId}/customers` })
        .then(json => json.data || [])
    },
    listItems (context, { businessEntityId }) {
      return api.doGet({ path: `${PATH_QUICKBOOKS_CONNECTIONS}/${businessEntityId}/items` })
        .then(json => json.data || [])
    },
    setExpenseItem ({ commit, dispatch }, { businessEntityId, expenseItemId }) {
      return withProgress(
        dispatch,
        'Saving the expense Item...',
        () => api.doPut({
          path: `${PATH_QUICKBOOKS_CONNECTIONS}/${businessEntityId}/expense-item`,
          data: { expenseItemId }
        }),
        'Unable to save the expense Item.'
      ).then(connection => {
        commit('setConnection', connection)
        return connection
      })
    }
  }
}

export default quickBooksConnections
